// client/src/components/canvas/DebugSnapshotPopover.tsx
//
// Admin-only popover for generating a debug snapshot of a canvas image.
// Shown when the admin clicks the `</>` hover button on an ImageNode.
// Lets the admin type a comment about what's wrong, then renders a
// markdown blob (comment + all DB context) that the admin copies into
// Claude Code.
//
// No persistence anywhere — close = gone. The snapshot is generated
// on-demand by GET /api/admin/canvas/image/:clientId/snapshot.

import { useCallback, useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Code2, Copy, Loader2, X, AlertCircle } from "lucide-react";
import { AdminSourcePreview } from "./AdminSourcePreview";
import { useToast } from "@/hooks/use-toast";

interface DebugSnapshotPopoverProps {
  clientId: string;
  projectId: number;
  // Pass through so the trigger can sit inside an ImageNode without dictating
  // its position — ImageNode is responsible for the chrome.
  trigger: React.ReactNode;
}

type FetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; markdown: string }
  | { status: "error"; message: string };

export function DebugSnapshotPopover({ clientId, projectId, trigger }: DebugSnapshotPopoverProps) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [state, setState] = useState<FetchState>({ status: "idle" });
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const { toast } = useToast();

  // Fetch the snapshot when the popover first opens. We use a ref to track
  // whether we've already started a fetch for this open-cycle — the
  // alternative (gating on state.status in the deps) makes the effect
  // re-run when setState({status:"loading"}) lands, the cleanup runs and
  // sets cancelled=true, and when the fetch finally resolves it bails out
  // → state is stuck on "loading" forever. The user-visible symptom was
  // "spinning in preview, can't click copy."
  const inFlightRef = useRef<{ clientId: string; projectId: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    // Already loaded or already in-flight for this (clientId, projectId) — skip.
    const sig = inFlightRef.current;
    if (sig && sig.clientId === clientId && sig.projectId === projectId) return;
    inFlightRef.current = { clientId, projectId };
    let cancelled = false;
    setState({ status: "loading" });
    fetch(
      `/api/admin/canvas/image/${encodeURIComponent(clientId)}/snapshot?projectId=${projectId}`,
      { credentials: "include" },
    )
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<{ markdown: string }>;
      })
      .then((body) => {
        if (cancelled) return;
        setState({ status: "loaded", markdown: body.markdown });
        // Focus the comment box once the snapshot loads.
        requestAnimationFrame(() => commentRef.current?.focus());
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: "error", message: err instanceof Error ? err.message : String(err) });
        // Clear the in-flight marker so a re-open can retry.
        inFlightRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId, projectId]);

  // Reset transient state when the popover closes so the next open starts
  // fresh — covers both the explicit Close button and click-outside paths.
  useEffect(() => {
    if (open) return;
    inFlightRef.current = null;
    setState({ status: "idle" });
    setComment("");
  }, [open]);

  const composeOutput = useCallback((): string => {
    const trimmed = comment.trim();
    const md = state.status === "loaded" ? state.markdown : "";
    if (!trimmed && !md) return "";
    if (!trimmed) return md;
    if (!md) {
      return `## Admin comment\n\n> ${trimmed.replace(/\n/g, "\n> ")}\n`;
    }
    // Inject the comment as a section near the top of the markdown.
    // The server snapshot already starts with the # title + Generated line,
    // so splice the comment in right after them.
    const headerEnd = md.indexOf("\n\n");
    if (headerEnd < 0) {
      return `## Admin comment\n\n> ${trimmed.replace(/\n/g, "\n> ")}\n\n${md}`;
    }
    return (
      md.slice(0, headerEnd + 2) +
      `## Admin comment\n\n> ${trimmed.replace(/\n/g, "\n> ")}\n\n` +
      md.slice(headerEnd + 2)
    );
  }, [comment, state]);

  const handleCopy = useCallback(async () => {
    const text = composeOutput();
    if (!text) {
      toast({ title: "Nothing to copy yet", variant: "destructive" });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      const kb = (text.length / 1024).toFixed(1);
      toast({
        title: `Copied ${kb} KB`,
        description: "Paste it into Claude Code.",
      });
      // Auto-close after a successful copy so the next click on another tile
      // starts fresh.
      setOpen(false);
      // Reset state so re-opening starts clean (especially relevant after
      // closing without copy).
      setComment("");
      setState({ status: "idle" });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  }, [composeOutput, toast]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="w-[480px] p-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Code2 size={14} />
            Debug snapshot
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-gray-400 hover:text-gray-700"
            aria-label="Close debug snapshot"
          >
            <X size={14} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {open && <AdminSourcePreview key={`${projectId}:${clientId}`} clientId={clientId} projectId={projectId} />}
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              What's wrong with this ad?
            </label>
            <textarea
              ref={commentRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Packaging is grey, should be azure blue…"
              rows={3}
              className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-gray-400 resize-none"
            />
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-gray-600 select-none">
              Preview ({state.status === "loaded" ? `${(state.markdown.length / 1024).toFixed(1)} KB` : state.status})
            </summary>
            <div className="mt-2 border border-gray-100 rounded bg-gray-50 p-2 max-h-72 overflow-y-auto font-mono text-[11px] text-gray-700 whitespace-pre-wrap">
              {state.status === "loading" && (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 size={12} className="animate-spin" />
                  Loading snapshot…
                </div>
              )}
              {state.status === "error" && (
                <div className="flex items-start gap-2 text-red-600">
                  <AlertCircle size={12} className="mt-0.5 shrink-0" />
                  <span>Failed to load: {state.message}</span>
                </div>
              )}
              {state.status === "loaded" && state.markdown}
            </div>
          </details>
        </div>

        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            size="sm"
            onClick={handleCopy}
            disabled={state.status === "loading"}
            className="gap-1.5"
          >
            <Copy size={12} />
            Copy to clipboard
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
